import {useAppSelector} from "../redux/hooks";
import {microphoneStore} from "../redux/microphone/microphoneStore";
import nativeBridge from "../services/nativeBridge";
import {logger} from "../services/log/logger";
import {displayErrorToUser} from "../services/error/error";
import {MicrophonePermissionStatus} from "../redux/types/MicrophonePermissionStatus";

/**
 * Responsibility: This file manages all interaction with the microphone.
 * - Checking permissions
 * - Requesting permissions
 * - Getting access to a stream of audio from the microphone
 * It is not responsible for recording audio.
 *
 * It currently performs its responsibility with multiple coding paradigms (e.g. hook, class, etc.)
 * In the long run, this should only be a hook.
 * However, to function with the event-based code throughout the app, its functionality is also exported as a class.
 */

interface IMicrophoneHook {
  checkPermissions: (doShowErrors?: boolean) => void;
  permissionStatus: MicrophonePermissionStatus;
  isAvailable: boolean;
}

export const useMicrophone = (): IMicrophoneHook => {
  const permissionStatus = useAppSelector(microphoneStore.getPermissionStatus);
  const isMicrophoneAvailable = useAppSelector(microphoneStore.getIsAvailable);

  return {
    checkPermissions: microphone.checkPermissions,
    permissionStatus: permissionStatus,
    isAvailable: isMicrophoneAvailable,
  };
}

class Microphone {
  /**
    * Asks the user for permission to access the microphone if they haven't been asked before. Also gets a stream
   * of audio from the microphone.
   */
  async getAccess(constraints?: MediaStreamConstraints | undefined): Promise<MediaStream> {
    return new Promise((resolve, reject) => {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        navigator.mediaDevices.getUserMedia(constraints)
          .then((stream) => {
            microphoneStore.setPermissionStatus("granted");
            resolve(stream);
          })
          .catch((error) => {
            microphoneStore.setPermissionStatus("refused");
            reject(error);
          });
      } else {
        microphoneStore.setPermissionStatus("refused");
        reject("getUserMedia not supported");
      }
    });
  }

  /** Asks what the permissions are and updates redux with the permissions */
  async checkPermissions(doShowErrors: boolean = false): Promise<MicrophonePermissionStatus> {
    const isInBrowser = !nativeBridge.isRunningInNativeContainer;

    // Browsers don't have a native bridge we can call, so just hard code the permission status.
    if (isInBrowser) {
      microphoneStore.setPermissionStatus("requestable");
      return "requestable";
    }

    const nativePermissionStatus = await nativeBridge.checkPermissions();

    // NOTE: We can't currently distinguish b/t modern versions of macOS & iPadOS, since Apple uses the same user agent for both.
    switch(nativePermissionStatus.microphone) {
      case "restricted": // The user cannot grant permissions for some reason (e.g. parental controls, or the user is on iOS/iPadOS and the app is not allowed to request permissions)
        // Pass through
      case "timeout": // The native app does not support microphone permissions
        microphoneStore.setPermissionStatus("unrequestable");
        return "unrequestable"
      case "blocked": // The user actively denied permissions on iOS.
        if (doShowErrors) {
          displayErrorToUser("Please allow microphone permissions to enable pitch detection");
        }
        microphoneStore.setPermissionStatus("refused");
        return "refused";
      case "unavailable":
        if (doShowErrors) {
          displayErrorToUser("No microphone was found. Plug one in.");
        }
        microphoneStore.setPermissionStatus("refused");
        return "refused";
      case "granted":
        microphoneStore.setPermissionStatus("granted");
        return "granted";
      case "not-determined":
        // Pass through
      case "denied": // The user is on Android and may/not have been prompted OR is on iOS/iPadOS and hasn't been prompted.
        microphoneStore.setPermissionStatus("requestable");
        return "requestable";
      default:
        logger.pitchDetection.error({
          message: "Unknown microphone permission status",
          data: [
            {key: "microphone permission status", value: nativePermissionStatus.microphone}
          ]
        });
        microphoneStore.setPermissionStatus("refused");
        return "refused";
    }
  }
}

export const microphone = new Microphone();
